import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  useTheme,
  alpha,
} from '@mui/material';
import {
  PushPin as PinIcon,
  Chat as ChatIcon,
  ScreenShare as ScreenShareIcon,
  Inbox as InboxIcon,
} from '@mui/icons-material';
import { useAppSelector } from '../store';
import ConversationItem from './ConversationItem';
import { Conversation } from '../store';

interface ConversationHistoryListProps {
  onSelect: (id: string) => void;
  onPin: (id: string) => void;
  onUnpin: (id: string) => void;
  onRename: (id: string, newTitle: string) => void;
  onDelete: (id: string) => void;
  onArchive: (id: string) => void;
  onLoadMore?: () => void;
}

interface GroupedConversations {
  pinned: Conversation[];
  recent: Conversation[];
  older: Conversation[];
}

const ConversationHistoryList: React.FC<ConversationHistoryListProps> = ({
  onSelect,
  onPin,
  onUnpin,
  onRename,
  onDelete,
  onArchive,
  onLoadMore,
}) => {
  const theme = useTheme();
  const { isCollapsed } = useAppSelector((state) => state.sidebar);
  const {
    items,
    ids,
    pinnedIds,
    searchResults,
    isLoading,
    error,
    activeConversationId,
    searchTerm,
    groupBy,
  } = useAppSelector((state) => state.conversations);





  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Group conversations based on current grouping
  const groupedConversations = useMemo((): GroupedConversations => {
    const conversations = searchTerm && searchResults.length > 0
      ? searchResults.map(id => items[id]).filter(Boolean)
      : ids.map(id => items[id]).filter(Boolean);

    const pinned = conversations.filter(conv => conv.isPinned);
    const unpinned = conversations.filter(conv => !conv.isPinned);

    // Sort by last activity
    const sortedUnpinned = unpinned.sort((a, b) => {
      const aDate = new Date(a.metadata?.lastActivity || a.updatedAt);
      const bDate = new Date(b.metadata?.lastActivity || b.updatedAt);
      return bDate.getTime() - aDate.getTime();
    });

    // Split into recent (last 7 days) and older
    const now = new Date();
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    const recent = sortedUnpinned.filter(conv => {
      const convDate = new Date(conv.metadata?.lastActivity || conv.updatedAt);
      return convDate >= sevenDaysAgo;
    });

    const older = sortedUnpinned.filter(conv => {
      const convDate = new Date(conv.metadata?.lastActivity || conv.updatedAt);
      return convDate < sevenDaysAgo;
    });

    return { pinned, recent, older };
  }, [items, ids, pinnedIds, searchResults, searchTerm]);

  // Handle infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      const container = document.querySelector('.conversation-list-container');
      if (!container || !onLoadMore || isLoadingMore) return;

      const { scrollTop, scrollHeight, clientHeight } = container as HTMLElement;
      const threshold = 100; // pixels from bottom

      if (scrollTop + clientHeight >= scrollHeight - threshold) {
        setIsLoadingMore(true);
        onLoadMore();
        // Reset loading state after a delay
        setTimeout(() => setIsLoadingMore(false), 1000);
      }
    };

    const container = document.querySelector('.conversation-list-container');
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [onLoadMore, isLoadingMore]);

  const renderGroupHeader = (title: string, count: number, icon?: React.ReactNode) => {
    if (isCollapsed) return null;

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1,
          backgroundColor: alpha(theme.palette.background.default, 0.5),
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        {icon && (
          <Box sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
            {icon}
          </Box>
        )}
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}
        >
          {title}
        </Typography>
        <Chip
          label={count}
          size="small"
          variant="outlined"
          sx={{
            height: 16,
            fontSize: '0.625rem',
            '& .MuiChip-label': {
              px: 0.5,
            },
          }}
        />
      </Box>
    );
  };

  const renderConversationList = (conversations: Conversation[], showHeader = true) => {
    if (conversations.length === 0) return null;

    return (
      <Box>
        {showHeader && !isCollapsed && (
          <Divider sx={{ my: 1 }} />
        )}
        {conversations.map((conversation) => (
          <ConversationItem
            key={conversation._id}
            conversation={conversation}
            isActive={activeConversationId === conversation._id}
            isCollapsed={isCollapsed}
            onSelect={onSelect}
            onPin={onPin}
            onUnpin={onUnpin}
            onRename={onRename}
            onDelete={onDelete}
            onArchive={onArchive}
          />
        ))}
      </Box>
    );
  };

  // Loading state
  if (isLoading && ids.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4,
          gap: 2,
        }}
      >
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary">
          Loading conversations...
        </Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ fontSize: '0.875rem' }}>
          {error}
        </Alert>
      </Box>
    );
  }

  // Empty state
  if (ids.length === 0 && !isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4,
          gap: 2,
          textAlign: 'center',
        }}
      >
        <InboxIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
        <Typography variant="body2" color="text.secondary">
          {searchTerm ? 'No conversations found' : 'No conversations yet'}
        </Typography>
        {!searchTerm && (
          <Typography variant="caption" color="text.secondary">
            Start a new conversation to get started
          </Typography>
        )}
      </Box>
    );
  }

  const { pinned, recent, older } = groupedConversations;
  const totalConversations = pinned.length + recent.length + older.length;

  return (
    <Box
      className="conversation-list-container"
      sx={{
        flex: 1,
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Search results indicator */}
      {searchTerm && (
        <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="body2" color="text.secondary">
            {totalConversations} result{totalConversations !== 1 ? 's' : ''} for "{searchTerm}"
          </Typography>
        </Box>
      )}

      {/* Pinned conversations */}
      {pinned.length > 0 && (
        <Box>
          {renderGroupHeader('Pinned', pinned.length, <PinIcon />)}
          {renderConversationList(pinned, false)}
        </Box>
      )}

      {/* Recent conversations */}
      {recent.length > 0 && (
        <Box>
          {renderGroupHeader('Recent', recent.length, <ChatIcon />)}
          {renderConversationList(recent, false)}
        </Box>
      )}

      {/* Older conversations */}
      {older.length > 0 && (
        <Box>
          {renderGroupHeader('Older', older.length, <ScreenShareIcon />)}
          {renderConversationList(older, false)}
        </Box>
      )}

      {/* Load more indicator */}
      {isLoadingMore && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            py: 2,
          }}
        >
          <CircularProgress size={20} />
        </Box>
      )}
    </Box>
  );
};

export default ConversationHistoryList; 